import { useEffect, useState, useCallback } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Copy, Check, Loader2, Download, Mail } from "lucide-react";
import jsPDF from "jspdf";
import { getCheckoutStatus, generateSpeech, sendEmail } from "@/lib/api";
import { FadeIn } from "@/components/FadeIn";
import { Logo } from "@/components/Logo";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const ROLES = [
  "Ehefrau", "Ehemann", "Tochter", "Sohn", "Schwester", "Bruder",
  "Freund:in", "Kolleg:in", "Enkel:in", "Trauerredner:in",
];

const SETTINGS = [
  "Trauerfeier",
  "Beerdigung",
  "Urnenbeisetzung",
  "Gedenkfeier",
  "Stille Abschiedsfeier",
];

const ADDRESS = ["Sie", "Du"];

const LENGTH = ["Kurz (2–3 Min)", "Mittel (4–5 Min)", "Ausführlich (6–8 Min)"];
const LENGTH_PREMIUM = [...LENGTH, "Umfangreich (9–14 Min)"];

const PERSONALITY = [
  "Ruhig", "Humorvoll", "Liebevoll", "Pflichtbewusst", "Großzügig",
  "Eigensinnig", "Tiefgründig", "Lebensfroh", "Bescheiden", "Mutig", "Verlässlich", "Kreativ",
];

const STEPS = [
  { key: "role", label: "Ihre Rolle", question: "In welcher Rolle sprechen Sie?", type: "chips", options: ROLES },
  { key: "setting", label: "Anlass", question: "Bei welchem Anlass wird die Rede gehalten?", type: "chips", options: SETTINGS },
  { key: "address", label: "Anrede", question: "Wie möchten Sie die Trauergemeinde ansprechen?", type: "chips", options: ADDRESS },
  { key: "length", label: "Länge", question: "Wie lang soll die Rede ungefähr sein?", type: "chips", options: LENGTH },
  { key: "name_dates", label: "Name & Daten", question: "Wie hieß der Mensch — und welche Lebensdaten möchten Sie nennen?", type: "text", placeholder: "z. B. Maria Schneider, 1942 – 2024" },
  { key: "family_life", label: "Familie & Leben", question: "Was war das Familien- und Lebensbild dieses Menschen?", type: "textarea", placeholder: "Beruf, Familie, Wegbegleiter, Lebensstationen — in Ruhe und in Stichpunkten genügt." },
  { key: "personality", label: "Persönlichkeit", question: "Welche Wesenszüge möchten Sie würdigen?", type: "chips-multi", options: PERSONALITY },
  { key: "memory", label: "Eine Erinnerung", question: "Erzählen Sie eine Erinnerung, die diesen Menschen ausmacht.", type: "textarea", placeholder: "Ein Moment, eine Szene, eine kleine Geste — was bleibt Ihnen im Herzen?" },
];

function PaywallGate({ children, sessionId, onUnlocked }) {
  const [state, setState] = useState({ loading: true, unlocked: false, error: null });
  const navigate = useNavigate();

  const poll = useCallback(async (attempts = 0) => {
    if (attempts >= 8) {
      setState({ loading: false, unlocked: false, error: "Zahlung konnte nicht bestätigt werden." });
      return;
    }
    try {
      const data = await getCheckoutStatus(sessionId);
      if (data.payment_status === "paid") {
        setState({ loading: false, unlocked: true, error: null });
        if (onUnlocked) onUnlocked(data.package_id || "oratio_draft");
        return;
      }
      if (data.status === "expired") {
        setState({ loading: false, unlocked: false, error: "Die Zahlungssitzung ist abgelaufen." });
        return;
      }
      setTimeout(() => poll(attempts + 1), 2000);
    } catch (e) {
      setState({ loading: false, unlocked: false, error: "Verbindungsfehler." });
    }
  }, [sessionId, onUnlocked]);

  useEffect(() => {
    if (!sessionId) {
      setState({ loading: false, unlocked: false, error: "Keine Sitzung gefunden." });
      return;
    }
    poll(0);
  }, [sessionId, poll]);

  if (state.loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-cream px-6">
        <Loader2 className="w-7 h-7 text-umber animate-spin" strokeWidth={1.4} />
        <p className="mt-6 font-serif text-lg text-charcoal">Zahlung wird bestätigt…</p>
        <p className="mt-2 font-sans text-sm text-ink">Einen Moment, bitte.</p>
      </div>
    );
  }

  if (!state.unlocked) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-cream px-6 text-center">
        <h1 className="font-serif text-3xl text-charcoal">Zahlung konnte nicht verifiziert werden.</h1>
        <p className="mt-4 font-serif text-ink max-w-md">{state.error}</p>
        <button onClick={() => navigate("/")} className="btn-secondary mt-10">Zurück zur Startseite</button>
      </div>
    );
  }

  return children;
}

function Chip({ selected, onClick, children, testId }) {
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      className={`chip ${selected ? "chip-selected" : ""}`}
    >
      {children}
    </button>
  );
}

function StepView({ step, value, onChange }) {
  if (step.type === "chips") {
    return (
      <div className="flex flex-wrap gap-3">
        {step.options.map((opt) => (
          <Chip
            key={opt}
            testId={`chip-${step.key}-${opt}`}
            selected={value === opt}
            onClick={() => onChange(opt)}
          >
            {opt}
          </Chip>
        ))}
      </div>
    );
  }
  if (step.type === "chips-multi") {
    const arr = Array.isArray(value) ? value : [];
    const toggle = (opt) =>
      onChange(arr.includes(opt) ? arr.filter((x) => x !== opt) : [...arr, opt]);
    return (
      <div className="flex flex-wrap gap-3">
        {step.options.map((opt) => (
          <Chip
            key={opt}
            testId={`chip-${step.key}-${opt}`}
            selected={arr.includes(opt)}
            onClick={() => toggle(opt)}
          >
            {opt}
          </Chip>
        ))}
      </div>
    );
  }
  if (step.type === "text") {
    return (
      <input
        type="text"
        data-testid={`input-${step.key}`}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={step.placeholder}
        className="w-full bg-paper rounded-2xl px-6 py-5 font-serif text-lg text-charcoal
                   placeholder:text-ink/50 outline-none focus:ring-2 focus:ring-umber/40
                   shadow-card"
      />
    );
  }
  return (
    <textarea
      data-testid={`textarea-${step.key}`}
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      placeholder={step.placeholder}
      rows={8}
      className="w-full bg-paper rounded-2xl px-6 py-5 font-serif text-lg text-charcoal
                 placeholder:text-ink/50 outline-none focus:ring-2 focus:ring-umber/40
                 shadow-card resize-none leading-relaxed"
    />
  );
}

const TONES = [
  { key: "ruhig", label: "Ruhig" },
  { key: "persönlich", label: "Persönlich" },
  { key: "andächtig", label: "Andächtig" },
];

function EmailDialog({ open, onOpenChange, sessionId, text }) {
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !email.includes("@")) {
      toast.error("Bitte gültige E-Mail-Adresse angeben.");
      return;
    }
    setSending(true);
    try {
      await sendEmail({ session_id: sessionId, recipient_email: email, text });
      toast.success("E-Mail versendet.");
      onOpenChange(false);
      setEmail("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Versand fehlgeschlagen.");
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="email-dialog"
        aria-describedby="email-desc"
        className="bg-paper border-0 max-w-md rounded-3xl p-10"
      >
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-charcoal text-left">
            Entwurf per E-Mail senden
          </DialogTitle>
          <DialogDescription id="email-desc" className="font-serif text-base text-ink text-left mt-2">
            Wir senden Ihnen den Text einmalig zu. Die Adresse wird nicht gespeichert.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="mt-6 space-y-4">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="ihre@email.de"
            data-testid="email-input"
            required
            className="w-full bg-cream rounded-2xl px-5 py-4 font-serif text-lg text-charcoal
                       placeholder:text-ink/50 outline-none focus:ring-2 focus:ring-umber/40"
          />
          <button
            type="submit"
            disabled={sending}
            data-testid="email-send-button"
            className="btn-primary w-full disabled:opacity-60"
          >
            {sending ? "Wird gesendet…" : "Senden"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ResultView({ text, sessionId, formInput, onRegenerate, currentTone }) {
  const [copied, setCopied] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);

  const copy = async () => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success("In die Zwischenablage kopiert.");
    setTimeout(() => setCopied(false), 2200);
  };

  const downloadPdf = () => {
    const doc = new jsPDF({ unit: "pt", format: "a4" });
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 64;
    const maxWidth = pageWidth - margin * 2;

    doc.setFont("times", "normal");
    doc.setTextColor(44, 36, 32);

    // Header
    doc.setFontSize(10);
    doc.setTextColor(138, 96, 64);
    doc.text("ORATIO  ·  TRAUERREDE-ENTWURF", margin, 60, { charSpace: 2 });

    // Title
    doc.setFontSize(20);
    doc.setTextColor(44, 36, 32);
    doc.text("Ihr Entwurf", margin, 100);

    // Body
    doc.setFontSize(12);
    doc.setTextColor(44, 36, 32);
    const lines = doc.splitTextToSize(text, maxWidth);
    let y = 140;
    const lineHeight = 18;
    const pageHeight = doc.internal.pageSize.getHeight();
    lines.forEach((line) => {
      if (y > pageHeight - margin) {
        doc.addPage();
        y = margin;
      }
      doc.text(line, margin, y);
      y += lineHeight;
    });

    doc.save("oratiorede-entwurf.pdf");
    toast.success("PDF heruntergeladen.");
  };

  return (
    <FadeIn>
      <div className="max-w-3xl mx-auto px-6 md:px-12 py-24 md:py-32">
        <p className="label-eyebrow mb-6">Ihr Entwurf</p>
        <h1 className="font-serif text-4xl md:text-5xl text-charcoal leading-[1.1]">
          Ein erster Text. <span className="italic text-umber">In Ihren Worten.</span>
        </h1>
        <p className="mt-6 font-serif text-lg text-ink leading-relaxed">
          Lesen Sie in Ruhe. Ändern Sie, was sich nicht stimmig anfühlt. Lassen Sie das stehen, was Sie berührt.
        </p>

        <div
          data-testid="result-text"
          className="mt-12 bg-paper rounded-3xl p-10 md:p-14 shadow-card whitespace-pre-wrap
                     font-serif text-lg leading-[1.85] text-charcoal"
        >
          {text}
        </div>

        {/* Tone variants */}
        <div className="mt-12">
          <p className="label-eyebrow mb-4">Andere Variante erzeugen</p>
          <div className="flex flex-wrap gap-3" data-testid="tone-selector">
            {TONES.map((t) => (
              <button
                key={t.key}
                onClick={() => onRegenerate(t.key)}
                data-testid={`tone-${t.key}`}
                disabled={t.key === currentTone}
                className={`chip ${t.key === currentTone ? "chip-selected cursor-default" : ""}`}
              >
                {t.label}
              </button>
            ))}
          </div>
          <p className="font-sans text-xs text-ink/60 mt-3">
            Auf Wunsch erzeugen wir denselben Entwurf mit anderer Tonalität.
          </p>
        </div>

        <div className="mt-12 flex flex-col sm:flex-row gap-4 flex-wrap">
          <button onClick={copy} data-testid="result-copy-button" className="btn-primary">
            {copied ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
            {copied ? "Kopiert" : "Text kopieren"}
          </button>
          <button onClick={downloadPdf} data-testid="result-pdf-button" className="btn-secondary">
            <Download className="w-4 h-4 mr-2" />
            Als PDF speichern
          </button>
          <button
            onClick={() => setEmailOpen(true)}
            data-testid="result-email-button"
            className="btn-secondary"
          >
            <Mail className="w-4 h-4 mr-2" />
            Per E-Mail senden
          </button>
        </div>

        <div className="mt-16 bg-paper rounded-3xl p-8 md:p-10 shadow-card">
          <div className="flex flex-col md:flex-row md:items-center gap-6">
            <div className="flex-1">
              <p className="font-display tracking-[0.25em] text-[11px] uppercase text-umber mb-3">
                Persönliche Begleitung
              </p>
              <h3 className="font-serif text-2xl md:text-3xl text-charcoal leading-tight mb-3">
                Möchten Sie den Entwurf mit einer erfahrenen Trauerrednerin besprechen?
              </h3>
              <p className="font-serif text-base text-ink leading-relaxed">
                Persönlich, unverbindlich, in Ruhe. Ich melde mich bei Ihnen zurück und wir schauen gemeinsam,
                was Ihre Rede noch braucht.
              </p>
            </div>
            <a
              href="mailto:hallo@oratiorede.de?subject=Professionelle%20Begleitung%20-%20meine%20Rede&body=Guten%20Tag%2C%0A%0Aich%20habe%20mit%20Oratiorede%20einen%20Entwurf%20erstellt%20und%20wu%CC%88rde%20gerne%20professionelle%20Begleitung%20anfragen.%0A%0AVielen%20Dank%0A"
              data-testid="result-pro-button"
              className="btn-primary shrink-0"
            >
              Anfrage senden
            </a>
          </div>
        </div>

        <p className="mt-10 font-sans text-xs text-ink/60 text-center">
          Hinweis: Bitte sichern Sie den Text — er wird aus Datenschutzgründen nicht dauerhaft gespeichert.
        </p>
      </div>
      <EmailDialog open={emailOpen} onOpenChange={setEmailOpen} sessionId={sessionId} text={text} />
    </FadeIn>
  );
}

function GeneratorForm({ sessionId, packageId }) {
  const [stepIdx, setStepIdx] = useState(0);
  const [form, setForm] = useState({});
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState(null);
  const [currentTone, setCurrentTone] = useState("ruhig");

  const isPremium = packageId === "oratio_premium";
  const steps = STEPS.map((s) =>
    s.key === "length" ? { ...s, options: isPremium ? LENGTH_PREMIUM : LENGTH } : s
  );
  const step = steps[stepIdx];
  const value = form[step.key];

  const canProceed = (() => {
    if (step.type === "chips-multi") return Array.isArray(value) && value.length > 0;
    if (Array.isArray(value)) return value.length > 0;
    return Boolean(value && String(value).trim().length > 0);
  })();

  const next = () => {
    if (!canProceed) return;
    if (stepIdx < steps.length - 1) setStepIdx(stepIdx + 1);
    else submit();
  };

  const back = () => stepIdx > 0 && setStepIdx(stepIdx - 1);

  const buildPayload = (tone) => {
    // map length label to backend key
    const lengthMap = {
      "Kurz (2–3 Min)": "kurz",
      "Mittel (4–5 Min)": "mittel",
      "Ausführlich (6–8 Min)": "ausführlich",
      "Umfangreich (9–14 Min)": "umfangreich",
    };
    return {
      session_id: sessionId,
      role: form.role || "",
      setting: form.setting || "",
      name_dates: form.name_dates || "",
      family_life: form.family_life || "",
      personality: Array.isArray(form.personality) ? form.personality.join(", ") : (form.personality || ""),
      memory: form.memory || "",
      tone,
      length: lengthMap[form.length] || "mittel",
      address: form.address || "Sie",
    };
  };

  const submit = async (tone = "ruhig") => {
    setGenerating(true);
    try {
      const data = await generateSpeech(buildPayload(tone));
      setResult(data.text);
      setCurrentTone(tone);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      console.error(e);
      toast.error("Generierung fehlgeschlagen. Bitte erneut versuchen.");
    } finally {
      setGenerating(false);
    }
  };

  const regenerate = (tone) => {
    if (tone === currentTone) return;
    submit(tone);
  };

  if (result) {
    return (
      <ResultView
        text={result}
        sessionId={sessionId}
        formInput={form}
        currentTone={currentTone}
        onRegenerate={regenerate}
      />
    );
  }

  if (generating) {
    return (
      <div className="min-h-[80vh] flex flex-col items-center justify-center px-6">
        <Loader2 className="w-7 h-7 text-umber animate-spin" strokeWidth={1.4} />
        <p className="mt-6 font-serif text-xl text-charcoal">Ihr Entwurf entsteht…</p>
        <p className="mt-2 font-sans text-sm text-ink">Einen ruhigen Moment, bitte.</p>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-6 md:px-12 py-24 md:py-32">
      {/* Step indicator */}
      <div className="flex items-center gap-2 mb-12" data-testid="step-indicator">
        {steps.map((_, i) => (
          <div
            key={i}
            className={`h-px flex-1 transition-colors duration-500 ${
              i <= stepIdx ? "bg-umber" : "bg-umber/15"
            }`}
          />
        ))}
      </div>

      <div className="flex items-center justify-between mb-3">
        <p className="label-eyebrow">
          Schritt {stepIdx + 1} von {steps.length}
        </p>
        <p className="font-display tracking-[0.25em] text-[10px] text-umber">{step.label}</p>
      </div>

      <div key={step.key}>
        <h1
          data-testid={`step-question-${step.key}`}
          className="font-serif text-3xl md:text-4xl text-charcoal leading-[1.15] mb-10"
        >
          {step.question}
        </h1>

        <StepView
          step={step}
          value={value}
          onChange={(v) => setForm((f) => ({ ...f, [step.key]: v }))}
        />
      </div>

      <div className="mt-14 flex items-center justify-between">
        <button
          onClick={back}
          disabled={stepIdx === 0}
          data-testid="step-back"
          className="inline-flex items-center gap-2 font-sans text-sm text-ink
                     disabled:opacity-30 disabled:cursor-not-allowed hover:text-charcoal transition-colors"
        >
          <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
          Zurück
        </button>

        <button
          onClick={next}
          disabled={!canProceed}
          data-testid="step-next"
          className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {stepIdx === steps.length - 1 ? "Entwurf erzeugen" : "Weiter"}
          <ArrowRight className="w-4 h-4 ml-2" strokeWidth={1.5} />
        </button>
      </div>
    </div>
  );
}

export default function Generator() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [packageId, setPackageId] = useState("oratio_draft");

  return (
    <div className="bg-cream min-h-screen">
      <header className="absolute top-0 left-0 right-0 z-40">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-6 flex items-center justify-between">
          <Link to="/" data-testid="gen-logo">
            <Logo />
          </Link>
          <Link to="/" className="font-sans text-sm text-ink hover:text-charcoal">
            Startseite
          </Link>
        </div>
      </header>
      <PaywallGate sessionId={sessionId} onUnlocked={setPackageId}>
        <GeneratorForm sessionId={sessionId} packageId={packageId} />
      </PaywallGate>
    </div>
  );
}
